export const DEFAULT_GRID_SIZE = 4

export function snapValue(value, gridSize = DEFAULT_GRID_SIZE) {
  const size = Math.max(1, gridSize || DEFAULT_GRID_SIZE)
  return Math.round(value / size) * size
}

export function snapLayout(layout, gridSize = DEFAULT_GRID_SIZE) {
  return {
    x: snapValue(layout.x, gridSize),
    y: snapValue(layout.y, gridSize),
    w: Math.max(gridSize, snapValue(layout.w, gridSize)),
    h: Math.max(gridSize, snapValue(layout.h, gridSize)),
  }
}

export function rectanglesIntersect(first, second) {
  return (
    first.x < second.x + second.w &&
    first.x + first.w > second.x &&
    first.y < second.y + second.h &&
    first.y + first.h > second.y
  )
}

export function marqueeSelection(rectangle, widgets) {
  return widgets.filter((widget) => rectanglesIntersect(rectangle, widget.layout)).map((widget) => widget.id)
}

export function layoutsCollide(layouts, ignoredIds = []) {
  const ignored = new Set(ignoredIds)
  const candidates = layouts.filter((item) => !ignored.has(item.id))
  return candidates.some((item, index) =>
    candidates.slice(index + 1).some((other) => rectanglesIntersect(item.layout, other.layout)),
  )
}

export function placementCollides(layout, widgets, ignoredIds = []) {
  const ignored = new Set(ignoredIds)
  return widgets.some((widget) => !ignored.has(widget.id) && rectanglesIntersect(layout, widget.layout))
}

export function boundedGroupMove(selected, delta, canvas) {
  if (!selected.length) return { x: 0, y: 0 }
  const left = Math.min(...selected.map((item) => item.layout.x))
  const top = Math.min(...selected.map((item) => item.layout.y))
  const right = Math.max(...selected.map((item) => item.layout.x + item.layout.w))
  const bottom = Math.max(...selected.map((item) => item.layout.y + item.layout.h))
  return {
    x: Math.max(-left, Math.min(canvas.width - right, delta.x)) || 0,
    y: Math.max(-top, Math.min(canvas.height - bottom, delta.y)) || 0,
  }
}

export function alignmentGuides(layout, widgets, threshold = DEFAULT_GRID_SIZE) {
  const movingX = [layout.x, layout.x + layout.w / 2, layout.x + layout.w]
  const movingY = [layout.y, layout.y + layout.h / 2, layout.y + layout.h]
  const targetX = widgets.flatMap((widget) => [
    widget.layout.x,
    widget.layout.x + widget.layout.w / 2,
    widget.layout.x + widget.layout.w,
  ])
  const targetY = widgets.flatMap((widget) => [
    widget.layout.y,
    widget.layout.y + widget.layout.h / 2,
    widget.layout.y + widget.layout.h,
  ])
  const nearest = (moving, targets) =>
    moving
      .flatMap((value) => targets.map((target) => ({ offset: target - value, position: target })))
      .filter((item) => Math.abs(item.offset) <= threshold)
      .sort((first, second) => Math.abs(first.offset) - Math.abs(second.offset))[0]
  const vertical = nearest(movingX, targetX)
  const horizontal = nearest(movingY, targetY)
  return {
    x: layout.x + (vertical?.offset ?? 0),
    y: layout.y + (horizontal?.offset ?? 0),
    vertical: vertical?.position,
    horizontal: horizontal?.position,
  }
}

export function nearestFreePlacement(layout, widgets, canvasWidth, gridSize = DEFAULT_GRID_SIZE) {
  const start = snapLayout(layout, gridSize)
  const maxX = Math.max(0, snapValue(canvasWidth - start.w, gridSize))
  const startX = Math.max(0, Math.min(maxX, start.x))
  const startY = Math.max(0, start.y)
  if (!placementCollides({ ...start, x: startX, y: startY }, widgets)) return { ...start, x: startX, y: startY }
  const columns = Math.floor(maxX / gridSize) + 1
  const maxAttempts = Math.max(columns * 500, 1000)
  for (let attempt = 1; attempt < maxAttempts; attempt += 1) {
    const index = Math.round(startY / gridSize) * columns + Math.round(startX / gridSize) + attempt
    const candidate = { ...start, x: (index % columns) * gridSize, y: Math.floor(index / columns) * gridSize }
    if (!placementCollides(candidate, widgets)) return candidate
  }
  return { ...start, x: startX, y: startY }
}

export function canvasAutoHeight(currentHeight, widgets, padding = 24, gridSize = DEFAULT_GRID_SIZE) {
  const bottom = widgets.reduce((value, widget) => Math.max(value, widget.layout.y + widget.layout.h), 0)
  return Math.max(currentHeight, snapValue(bottom + padding, gridSize))
}
